// Trade Modal - Professional Order Form
import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { X, TrendingUp, TrendingDown, Zap, AlertTriangle, Wallet, Info } from 'lucide-react';
import { tradingAPI, userAPI } from '../../services/api';
import { useTradingToast } from '../../hooks/useTradingToast';

const TradeModal = ({ 
  isOpen, 
  onClose, 
  symbol, 
  currentPrice, 
  tradeMode,
  onSuccess 
}) => {
  const [side, setSide] = useState('long');
  const [orderType, setOrderType] = useState('market');
  const [amount, setAmount] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [leverage, setLeverage] = useState(10);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [takeProfit, setTakeProfit] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const { toast } = useTradingToast();

  const baseAsset = symbol?.replace('USDT', '') || 'BTC';
  const isLong = side === 'long' || side === 'buy';
  const isFutures = tradeMode === 'futures';

  useEffect(() => {
    if (isOpen) {
      loadBalance();
      setAmount('');
      setLimitPrice('');
      setTakeProfit('');
      setStopLoss('');
      setSide(isFutures ? 'long' : 'buy');
    }
  }, [isOpen, isFutures]);

  const loadBalance = async () => {
    try {
      const res = await userAPI.getBalance();
      setBalance(res.data);
    } catch (err) {
      console.error('Failed to load balance:', err);
    }
  };

  const handleQuickAmount = (percent) => {
    if (!balance?.exchange_balance) return;
    const amt = (balance.exchange_balance * percent / 100).toFixed(2);
    setAmount(amt);
  };

  const calculatePosition = () => {
    const margin = parseFloat(amount) || 0;
    const price = orderType === 'limit' ? parseFloat(limitPrice) : currentPrice;
    if (!price || price <= 0 || !margin) return { size: 0, liqPrice: 0, totalValue: 0 };

    if (isFutures) {
      const size = (margin * leverage) / price;
      const liqPrice = isLong
        ? price * (1 - 1 / leverage * 0.8)
        : price * (1 + 1 / leverage * 0.8);
      return { size, liqPrice, totalValue: margin * leverage };
    } else {
      // Spot: amount in USDT, get quantity
      const quantity = margin / price;
      return { size: quantity, liqPrice: 0, totalValue: margin };
    }
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Введите сумму');
      return;
    }

    const price = orderType === 'limit' ? parseFloat(limitPrice) : currentPrice;
    if (!price || price <= 0) {
      toast.priceUnavailable();
      return;
    }

    if (orderType === 'limit' && (!limitPrice || parseFloat(limitPrice) <= 0)) {
      toast.error('Введите лимитную цену');
      return;
    }

    setLoading(true);
    try {
      if (isFutures) {
        await tradingAPI.openTrade({
          symbol,
          type: 'futures',
          side,
          margin: parseFloat(amount),
          leverage,
          order_type: orderType,
          limit_price: orderType === 'limit' ? parseFloat(limitPrice) : null,
          take_profit: takeProfit ? parseFloat(takeProfit) : null,
          stop_loss: stopLoss ? parseFloat(stopLoss) : null,
        });

        if (orderType === 'limit') {
          toast.limitOrder(symbol, side, parseFloat(limitPrice));
        } else {
          toast.positionOpened(symbol, side, leverage, parseFloat(amount));
        }
      } else {
        // Spot trading
        await tradingAPI.spotBuy({
          symbol,
          amount_usdt: parseFloat(amount),
        });
        toast.success('Покупка выполнена', `Куплено ${baseAsset} на $${amount}`);
      }

      onClose();
      onSuccess?.();
    } catch (error) {
      toast.apiError(error, 'Ошибка при выполнении ордера');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const { size, liqPrice, totalValue } = calculatePosition();
  const availableBalance = balance?.exchange_balance || 0;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1e2329',
          borderRadius: '16px',
          width: '420px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.5)',
          border: '1px solid #2b3139',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #2b3139',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(180deg, #252930 0%, #1e2329 100%)',
        }}>
          <div>
            <h2 style={{ 
              margin: 0, 
              color: '#eaecef', 
              fontSize: '18px', 
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              {isFutures ? (
                <>
                  <Zap size={20} color="#f0b90b" />
                  Фьючерсы
                </>
              ) : (
                <>
                  <Wallet size={20} color="#0ecb81" />
                  Спот покупка
                </>
              )}
            </h2>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              marginTop: '4px',
            }}>
              <span style={{ color: '#848e9c', fontSize: '14px', fontWeight: '600' }}>{symbol}</span>
              <span style={{ 
                color: '#f0b90b', 
                fontSize: '16px', 
                fontWeight: '700',
                fontFamily: 'monospace',
              }}>
                ${currentPrice?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#2b3139',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              color: '#848e9c',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {/* Balance Info */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            backgroundColor: '#161a1e',
            borderRadius: '10px',
            marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Wallet size={16} color="#848e9c" />
              <span style={{ color: '#848e9c', fontSize: '13px' }}>Доступно:</span>
            </div>
            <span style={{ color: '#eaecef', fontSize: '15px', fontWeight: '700', fontFamily: 'monospace' }}>
              ${availableBalance.toFixed(2)} USDT
            </span>
          </div>

          {/* Side Selector - Only for Futures */}
          {isFutures && (
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              marginBottom: '16px',
            }}>
              <button
                onClick={() => setSide('long')}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: side === 'long'
                    ? 'linear-gradient(180deg, #0ecb81 0%, #0ba360 100%)'
                    : '#161a1e',
                  color: side === 'long' ? '#fff' : '#848e9c',
                  border: side === 'long' ? 'none' : '1px solid #2b3139',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                }}
              >
                <TrendingUp size={18} />
                Long / Покупка
              </button>
              <button
                onClick={() => setSide('short')}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: side === 'short'
                    ? 'linear-gradient(180deg, #f6465d 0%, #cf304a 100%)'
                    : '#161a1e',
                  color: side === 'short' ? '#fff' : '#848e9c',
                  border: side === 'short' ? 'none' : '1px solid #2b3139',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                }}
              >
                <TrendingDown size={18} />
                Short / Продажа
              </button>
            </div>
          )}

          {/* Order Type - Only for Futures */}
          {isFutures && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['market', 'limit'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setOrderType(type)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: orderType === type ? '#f0b90b20' : '#161a1e',
                      color: orderType === type ? '#f0b90b' : '#848e9c',
                      border: `1px solid ${orderType === type ? '#f0b90b' : '#2b3139'}`,
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    {type === 'market' ? 'Маркет' : 'Лимит'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Limit Price */}
          {isFutures && orderType === 'limit' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#848e9c', fontSize: '12px', marginBottom: '6px', display: 'block' }}>
                Лимитная цена
              </label>
              <input
                type="number"
                value={limitPrice}
                onChange={(e) => setLimitPrice(e.target.value)}
                placeholder={`${currentPrice?.toFixed(2) || '0.00'}`}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  backgroundColor: '#161a1e',
                  border: '1px solid #2b3139',
                  borderRadius: '10px',
                  color: '#eaecef',
                  fontSize: '15px',
                  fontFamily: 'monospace',
                  outline: 'none',
                }}
              />
            </div>
          )}

          {/* Amount Input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: '#848e9c', fontSize: '12px', marginBottom: '6px', display: 'block' }}>
              {isFutures ? 'Маржа (USDT)' : 'Сумма покупки (USDT)'}
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              style={{
                width: '100%',
                padding: '14px 16px',
                backgroundColor: '#161a1e',
                border: '1px solid #2b3139',
                borderRadius: '10px',
                color: '#eaecef',
                fontSize: '18px',
                fontFamily: 'monospace',
                fontWeight: '600',
                outline: 'none',
              }}
            />
            {/* Quick Amount Buttons */}
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
              {[25, 50, 75, 100].map((pct) => (
                <button
                  key={pct}
                  onClick={() => handleQuickAmount(pct)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    backgroundColor: '#161a1e',
                    border: '1px solid #2b3139',
                    borderRadius: '6px',
                    color: '#848e9c',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#252930';
                    e.currentTarget.style.color = '#f0b90b';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#161a1e';
                    e.currentTarget.style.color = '#848e9c';
                  }}
                >
                  {pct}%
                </button>
              ))}
            </div>
          </div>

          {/* Leverage Slider - Only for Futures */}
          {isFutures && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ color: '#848e9c', fontSize: '12px' }}>Плечо</label>
                <span style={{ color: '#f0b90b', fontSize: '14px', fontWeight: '700' }}>{leverage}x</span>
              </div>
              <input
                type="range"
                min="1"
                max="100"
                value={leverage}
                onChange={(e) => setLeverage(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  height: '6px',
                  borderRadius: '3px',
                  background: `linear-gradient(to right, #f0b90b 0%, #f0b90b ${(leverage / 100) * 100}%, #2b3139 ${(leverage / 100) * 100}%, #2b3139 100%)`,
                  outline: 'none',
                  cursor: 'pointer',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                {[1, 5, 10, 25, 50, 100].map((lev) => (
                  <button
                    key={lev}
                    onClick={() => setLeverage(lev)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: leverage === lev ? '#f0b90b' : '#161a1e',
                      color: leverage === lev ? '#0b0e11' : '#848e9c',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    {lev}x
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* SL/TP for Futures */}
          {isFutures && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '10px', 
              marginBottom: '16px' 
            }}>
              <div>
                <label style={{ color: '#848e9c', fontSize: '11px', marginBottom: '4px', display: 'block' }}>
                  Take Profit
                </label>
                <input
                  type="number"
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  placeholder="Необязательно"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#161a1e',
                    border: '1px solid #2b3139',
                    borderRadius: '8px',
                    color: '#0ecb81',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    outline: 'none',
                  }}
                />
              </div>
              <div>
                <label style={{ color: '#848e9c', fontSize: '11px', marginBottom: '4px', display: 'block' }}>
                  Stop Loss
                </label>
                <input
                  type="number"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  placeholder="Необязательно"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#161a1e',
                    border: '1px solid #2b3139',
                    borderRadius: '8px',
                    color: '#f6465d',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    outline: 'none',
                  }}
                />
              </div>
            </div>
          )}

          {/* Order Summary */}
          <div style={{
            backgroundColor: '#161a1e',
            borderRadius: '10px',
            padding: '14px 16px',
            marginBottom: '16px',
          }}>
            <div style={{ fontSize: '12px', color: '#848e9c', marginBottom: '10px', fontWeight: '600' }}>
              Детали ордера
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#848e9c', fontSize: '13px' }}>
                {isFutures ? 'Размер позиции' : 'Количество'}
              </span>
              <span style={{ color: '#eaecef', fontSize: '13px', fontFamily: 'monospace', fontWeight: '600' }}>
                {currentPrice > 0 || (orderType === 'limit' && parseFloat(limitPrice) > 0)
                  ? `${size.toFixed(6)} ${baseAsset}`
                  : 'Загрузка цены...'
                }
              </span>
            </div>

            {isFutures && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#848e9c', fontSize: '13px' }}>Стоимость позиции</span>
                  <span style={{ color: '#eaecef', fontSize: '13px', fontFamily: 'monospace' }}>
                    ${totalValue.toFixed(2)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#848e9c', fontSize: '13px' }}>Цена ликвидации</span>
                  <span style={{ color: '#f6465d', fontSize: '13px', fontFamily: 'monospace', fontWeight: '600' }}>
                    ${liqPrice.toFixed(2)}
                  </span>
                </div>
              </>
            )}

            {!isFutures && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#848e9c', fontSize: '13px' }}>Цена покупки</span>
                <span style={{ color: '#eaecef', fontSize: '13px', fontFamily: 'monospace' }}>
                  ${currentPrice?.toFixed(2) || '0.00'}
                </span>
              </div>
            )}
          </div>

          {/* Spot Info Banner */}
          {!isFutures && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '12px',
              backgroundColor: '#0ecb8115',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #0ecb8130',
            }}>
              <Info size={16} color="#0ecb81" style={{ flexShrink: 0, marginTop: '2px' }} />
              <span style={{ fontSize: '12px', color: '#848e9c', lineHeight: '1.5' }}>
                Спот покупка — вы приобретаете реальный актив {baseAsset}, который будет добавлен в ваш спотовый кошелёк.
              </span>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading || !amount}
            style={{
              width: '100%',
              padding: '16px',
              background: loading || !amount
                ? '#2b3139'
                : isLong
                  ? 'linear-gradient(180deg, #0ecb81 0%, #0ba360 100%)'
                  : 'linear-gradient(180deg, #f6465d 0%, #cf304a 100%)',
              color: loading || !amount ? '#5e6673' : '#fff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading || !amount ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'all 0.2s',
              boxShadow: loading || !amount ? 'none' : isLong 
                ? '0 4px 20px rgba(14, 203, 129, 0.3)'
                : '0 4px 20px rgba(246, 70, 93, 0.3)',
            }}
          >
            {loading ? (
              'Выполнение...'
            ) : isFutures ? (
              <>
                {isLong ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                {isLong ? 'Открыть Long' : 'Открыть Short'}
              </>
            ) : (
              <>
                <Wallet size={20} />
                Купить {baseAsset}
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 6px;
          border-radius: 3px;
          outline: none;
          cursor: pointer;
        }
        input[type="range"]::-webkit-slider-runnable-track {
          height: 6px;
          border-radius: 3px;
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          background: #f0b90b;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(240, 185, 11, 0.4);
          margin-top: -6px;
        }
        input[type="range"]::-moz-range-track {
          height: 6px;
          border-radius: 3px;
          background: #2b3139;
        }
        input[type="range"]::-moz-range-thumb {
          width: 18px;
          height: 18px;
          background: #f0b90b;
          border-radius: 50%;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 8px rgba(240, 185, 11, 0.4);
        }
      `}</style>
    </div>
  );
};

export default TradeModal;
